import { Person } from "@mui/icons-material"
import Alert from "@mui/material/Alert"
import Card from "@mui/material/Card"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import React from "react"

import Layout from "@/components/layout"
import ReservationStatusLabel from "@/components/reservation_status_label"
import DateFilter, { DateFilterValue } from "@/components/reservations/date_filter"
import EditForm from "@/components/reservations/edit_form"
import StatusFilter from "@/components/reservations/status_filter"
import {
  STATUS_CANCELLED,
  STATUS_CONFIRMED,
  STATUS_NO_SHOW,
  STATUS_PENDING,
  STATUS_REMOVED,
  STATUS_WALK_IN,
} from "@/constants/reservations"
import { useVenue } from "@/contexts/venue"
import { ReservationDateField, ReservationStatus } from "@/graphql/graphql"
import { useReservations } from "@/graphql/hooks"
import i18n from "@/i18n"
import { timeRange } from "@/shared/datetime"
import { useReservationTabHydrate } from "@/shared/hooks"
import { TableSkeleton } from "@/shared/skeleton"
import { formatTableName } from "@/shared/tables"
import { useStore } from "@/store"
import { getDateFilterLabel } from "@/utils/date_filter"

const ALL_STATUSES = [
  STATUS_CONFIRMED,
  STATUS_WALK_IN,
  STATUS_PENDING,
  STATUS_NO_SHOW,
  STATUS_CANCELLED,
  STATUS_REMOVED,
]

const ReservationsAll = () => {
  const { venue } = useVenue()
  const [previewReservationId, setPreviewReservationId] = React.useState(null)
  const [statusFilter, setStatusFilter] = React.useState<string[]>(ALL_STATUSES)
  const { currentDate } = useStore()
  const [dateFilter, setDateFilter] = React.useState<DateFilterValue>({
    field: ReservationDateField.ReservationDate,
    rangeType: "range",
    from: currentDate?.toISODate() || undefined,
    to: currentDate?.toISODate() || undefined,
  })

  useReservationTabHydrate(venue)

  const includeRemoved = statusFilter.includes(STATUS_REMOVED)

  // Build the date filter for the query
  const buildDateFilterForQuery = () => {
    if (dateFilter.rangeType === "from" && dateFilter.from) {
      return {
        field: dateFilter.field,
        from: dateFilter.from,
      }
    }
    if (dateFilter.rangeType === "to" && dateFilter.to) {
      return {
        field: dateFilter.field,
        to: dateFilter.to,
      }
    }
    if (dateFilter.rangeType === "range" && dateFilter.from && dateFilter.to) {
      return {
        field: dateFilter.field,
        from: dateFilter.from,
        to: dateFilter.to,
      }
    }
    return undefined
  }

  const { reservations, loading, error } = useReservations({
    dateFilter: buildDateFilterForQuery(),
    status: statusFilter as ReservationStatus[],
    includeRemoved,
  })

  return (
    <Layout title={i18n.t("app.reservations_list.header")} flex data-testid="panel-reservations-all-page">
      <div className="flex items-end justify-between w-full">
        <div className="flex items-baseline gap-3">
          <h1 className="text-3xl mt-3 mb-0 font-medium">{i18n.t("app.reservations_list.header")}</h1>
          <Typography variant="body2" className="text-gray-600">
            {getDateFilterLabel(dateFilter)}
          </Typography>
        </div>

        <div className="flex gap-2">
          <DateFilter value={dateFilter} onChange={setDateFilter} />
          <StatusFilter statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
        </div>
      </div>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {JSON.stringify(error)}
        </Alert>
      ) : null}

      <Card variant="outlined" className="p-4 flex-1">
        <div className="mb-4" />

        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>Table</TableCell>
                <TableCell>Seats</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <TableSkeleton rows={3} columns={7} delay={100} fadingAway className="h-40" />
            ) : (
              <TableBody>
                {reservations?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center h-40">
                      {i18n.t("app.reservations_list.empty")}
                    </TableCell>
                  </TableRow>
                )}
                {reservations?.map((reservation) => (
                  <TableRow
                    key={reservation.id}
                    onClick={() => setPreviewReservationId(reservation.id)}
                    data-testid={`panel-reservations-all-row-${reservation.id}`}
                  >
                    <TableCell component="th" scope="row">
                      #{reservation.id}
                    </TableCell>
                    <TableCell>
                      <Typography>{reservation.customerContact?.name || "-"}</Typography>
                      <Typography variant="subtitle2">{reservation.customerContact?.email || "-"}</Typography>
                    </TableCell>
                    <TableCell>{reservation.customerContact?.phoneFormatted || "-"}</TableCell>
                    <TableCell>{timeRange(reservation.startTime, reservation.endTime)}</TableCell>
                    <TableCell>{formatTableName(reservation.table)}</TableCell>
                    <TableCell>
                      {reservation.peopleAmount}
                      <Person className="align-bottom ml-0.5" />
                    </TableCell>
                    <TableCell>
                      <ReservationStatusLabel status={reservation.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Card>

      <EditForm venue={venue} reservationId={previewReservationId} onClose={() => setPreviewReservationId(null)} />
    </Layout>
  )
}

export default ReservationsAll
